import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { SignIn, SignOut, Clock, ClockCounterClockwise, PencilSimple, ListBullets } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 2 — Attendance. Live check-in/check-out widget on top + history table
// below. Admin sees all-staff roster + summary; staff sees only own records.

const STATUS_STYLES = {
  present:     { label: 'Present',     cls: 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]' },
  absent:      { label: 'Absent',      cls: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]' },
  half_day:    { label: 'Half Day',    cls: 'bg-[#FFEDD5] text-[#9A3412] border-[#FDBA74]' },
  late:        { label: 'Late',        cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  weekly_off:  { label: 'Weekly Off',  cls: 'bg-[#E0E7FF] text-[#4338CA] border-[#C7D2FE]' },
  leave:       { label: 'Leave',       cls: 'bg-[#EDE9FE] text-[#6D28D9] border-[#DDD6FE]' },
  not_marked:  { label: 'Not Marked',  cls: 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]' },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }); }
  catch { return iso; }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

const StatusBadge = ({ status, isLate }) => {
  const meta = STATUS_STYLES[status] || STATUS_STYLES.not_marked;
  return (
    <div className="flex gap-1">
      <Badge className={`rounded-md text-[10px] ${meta.cls}`} variant="outline">{meta.label}</Badge>
      {isLate && status !== 'late' && (
        <Badge className={`rounded-md text-[10px] ${STATUS_STYLES.late.cls}`} variant="outline">Late</Badge>
      )}
    </div>
  );
};

// --- Punch widget (top card, always visible to logged-in user) ---
function PunchCard({ today, onPunched }) {
  const checkIn = async () => {
    try {
      await api.post('/attendance/checkin', { remark: '' });
      toast.success('Checked in');
      onPunched();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    }
  };
  const checkOut = async () => {
    try {
      await api.post('/attendance/checkout', { remark: '' });
      toast.success('Checked out');
      onPunched();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-out failed');
    }
  };

  const hasCheckedIn = !!today?.check_in;
  const hasCheckedOut = !!today?.check_out;

  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#A1A1AA]">Today</p>
            <p className="text-2xl font-bold text-[#0A0A0A] mt-0.5">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
            {today ? (
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                <StatusBadge status={today.status} isLate={today.is_late} />
                <div className="text-[#52525B]"><Clock size={14} className="inline mr-1" /> In: <span className="font-mono font-semibold text-[#0A0A0A]">{fmtTime(today.check_in)}</span></div>
                <div className="text-[#52525B]"><ClockCounterClockwise size={14} className="inline mr-1" /> Out: <span className="font-mono font-semibold text-[#0A0A0A]">{fmtTime(today.check_out)}</span></div>
                {today.total_working_hours > 0 && (
                  <div className="text-[#52525B]">Worked: <span className="font-mono font-semibold text-[#10B981]">{today.total_working_hours}h</span></div>
                )}
                {today.is_late && (
                  <div className="text-[#92400E]">Late: <span className="font-mono font-semibold">{today.late_minutes}m</span></div>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#A1A1AA] mt-2">No punch yet today.</p>
            )}
          </div>
          <div className="flex gap-2">
            {!hasCheckedIn && (
              <Button onClick={checkIn} className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-xl h-12 px-6 text-base font-semibold" data-testid="checkin-btn">
                <SignIn size={18} className="mr-2" /> Check In
              </Button>
            )}
            {hasCheckedIn && !hasCheckedOut && (
              <Button onClick={checkOut} className="bg-[#EF4444] text-white hover:bg-[#DC2626] hover:text-white rounded-xl h-12 px-6 text-base font-semibold" data-testid="checkout-btn">
                <SignOut size={18} className="mr-2" /> Check Out
              </Button>
            )}
            {hasCheckedIn && hasCheckedOut && (
              <div className="text-sm text-[#10B981] font-semibold flex items-center gap-2 h-12">
                <span>Day complete</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Today roster (admin only) ---
function TodayRoster({ data, onRefresh }) {
  if (!data) return null;
  const { summary, rows } = data;
  const counts = [
    { key: 'present', label: 'Present' },
    { key: 'absent', label: 'Absent' },
    { key: 'half_day', label: 'Half Day' },
    { key: 'late', label: 'Late' },
    { key: 'leave', label: 'On Leave' },
    { key: 'weekly_off', label: 'Weekly Off' },
    { key: 'not_marked', label: 'Not Marked' },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {counts.map(c => {
          const meta = STATUS_STYLES[c.key];
          return (
            <Card key={c.key} className="rounded-xl border-[#E4E4E7] shadow-sm">
              <CardContent className="p-3">
                <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#A1A1AA]">{c.label}</p>
                <p className={`text-2xl font-bold mt-0.5 ${meta?.cls.includes('text-') ? meta.cls.split(' ').find(x => x.startsWith('text-')) : ''}`}>{summary[c.key] ?? 0}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E4E4E7]">
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Staff</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">In</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Out</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Worked</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Late</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.user_id} className="border-[#E4E4E7]" data-testid={`roster-row-${r.user_id}`}>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-2.5">
                      {r.photo ? (
                        <img src={r.photo} alt={r.user_name} className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 bg-[#3B82F6] rounded-full flex items-center justify-center text-white text-[10px] font-bold">{(r.user_name || '?').charAt(0)}</div>
                      )}
                      <div>
                        <p className="font-medium">{r.user_name}</p>
                        <p className="text-[10px] text-[#A1A1AA]">{r.designation || '—'}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={r.status} isLate={r.is_late} /></TableCell>
                  <TableCell className="text-xs font-mono">{fmtTime(r.check_in)}</TableCell>
                  <TableCell className="text-xs font-mono">{fmtTime(r.check_out)}</TableCell>
                  <TableCell className="text-xs text-right font-mono">{r.total_working_hours ? `${r.total_working_hours}h` : '—'}</TableCell>
                  <TableCell className="text-xs text-right font-mono">{r.late_minutes ? `${r.late_minutes}m` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Admin edit dialog ---
function AttendanceEditDialog({ record, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: '', late_minutes: 0, total_working_hours: 0, remark: '', edit_reason: '',
  });

  useEffect(() => {
    if (record) {
      setForm({
        status: record.status || 'present',
        late_minutes: record.late_minutes || 0,
        total_working_hours: record.total_working_hours || 0,
        remark: record.remark || '',
        edit_reason: '',
      });
    }
  }, [record]);

  const save = async () => {
    if (!form.edit_reason.trim()) { toast.error('Edit reason is required'); return; }
    try {
      await api.put(`/attendance/${record.id}`, form);
      toast.success('Attendance updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Attendance — {record?.user_name}</DialogTitle>
          <DialogDescription>{record?.date} — changes are logged with reason in the audit history.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger className="rounded-lg mt-1" data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="present">Present</SelectItem>
                <SelectItem value="absent">Absent</SelectItem>
                <SelectItem value="half_day">Half Day</SelectItem>
                <SelectItem value="leave">Leave</SelectItem>
                <SelectItem value="weekly_off">Weekly Off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Late (minutes)</Label>
            <Input type="number" value={form.late_minutes} onChange={e => setForm({ ...form, late_minutes: parseInt(e.target.value) || 0 })} className="rounded-lg mt-1" data-testid="edit-late" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Worked Hours</Label>
            <Input type="number" step="0.25" value={form.total_working_hours} onChange={e => setForm({ ...form, total_working_hours: parseFloat(e.target.value) || 0 })} className="rounded-lg mt-1" data-testid="edit-hours" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Remark</Label>
            <Textarea rows={2} value={form.remark} onChange={e => setForm({ ...form, remark: e.target.value })} className="rounded-lg mt-1" data-testid="edit-remark" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Edit Reason *</Label>
            <Input value={form.edit_reason} onChange={e => setForm({ ...form, edit_reason: e.target.value })} className="rounded-lg mt-1" placeholder="e.g. Forgot to punch out — left at 6 PM" data-testid="edit-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-edit-att">
            Save with Audit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- History dialog ---
function HistoryDialog({ recordId, onClose }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (recordId) {
      api.get(`/attendance/${recordId}/history`).then(r => setHistory(r.data || [])).catch(() => {});
    }
  }, [recordId]);
  return (
    <Dialog open={!!recordId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit History</DialogTitle>
          <DialogDescription>Every admin override is recorded with reason.</DialogDescription>
        </DialogHeader>
        {history.length === 0 ? (
          <p className="text-sm text-[#52525B] py-3">No edits yet.</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {history.map((h, i) => (
              <div key={i} className="border border-[#E4E4E7] rounded-lg p-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-semibold text-[#0A0A0A]">{h.edited_by_name}</span>
                  <span className="text-[#A1A1AA]">{new Date(h.edited_at).toLocaleString('en-IN')}</span>
                </div>
                <p className="text-xs italic text-[#52525B] mb-2">"{h.reason}"</p>
                <div className="space-y-1">
                  {Object.entries(h.diffs || {}).map(([k, v]) => (
                    <p key={k} className="text-[11px] font-mono text-[#52525B]">
                      <span className="font-semibold">{k}:</span> {String(v.from)} → <span className="text-[#10B981]">{String(v.to)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MyStatsBar() {
  // Live monthly stats — re-uses the existing /staff-dashboard/me payload
  // already powering the My-Pulse widget on the My Work page. Renders only
  // for the staff/admin themselves on Attendance, above the Punch card.
  const [stats, setStats] = useState(null);
  const [policy, setPolicy] = useState('');
  useEffect(() => {
    api.get('/staff-dashboard/me').then(r => setStats(r.data)).catch(() => {});
    api.get('/settings/company').then(r => setPolicy(r.data.late_deduction_policy || '')).catch(() => {});
  }, []);
  if (!stats) return null;
  const m = stats.month_summary || {};
  const lb = stats.leave_balance || {};
  const tiles = [
    { label: 'Working Days', value: stats.working_days ?? 0, color: 'text-[#52525B]', bg: 'bg-[#F4F4F5]' },
    { label: 'Worked', value: m.present || 0, color: 'text-[#15803D]', bg: 'bg-[#DCFCE7]' },
    { label: 'Pending Off', value: Math.max(0, (stats.working_days || 0) - (m.present || 0) - (m.half_day || 0) - (m.leave || 0) - (m.weekly_off || 0)), color: 'text-[#9A3412]', bg: 'bg-[#FFEDD5]' },
    { label: 'Half Days', value: m.half_day || 0, color: 'text-[#9A3412]', bg: 'bg-[#FFEDD5]' },
    { label: 'Late Coming', value: m.late || 0, color: 'text-[#D97706]', bg: 'bg-[#FEF3C7]' },
    { label: 'Paid Leave Balance', value: `${lb.paid_remaining ?? 0}/${lb.paid_limit ?? 0}`, color: 'text-[#6D28D9]', bg: 'bg-[#EDE9FE]', mono: false },
  ];
  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="my-stats-bar">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Current Month · Live</p>
          <p className="text-[10px] text-[#A1A1AA]">{stats.month}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {tiles.map(t => (
            <div key={t.label} className={`rounded-lg px-3 py-2 text-center border border-[#E4E4E7] ${t.bg}`}>
              <p className="text-[9px] uppercase tracking-[0.1em] font-semibold text-[#52525B] truncate">{t.label}</p>
              <p className={`text-xl font-bold leading-tight mt-0.5 ${t.color} ${t.mono === false ? '' : 'font-mono'}`}>{t.value}</p>
            </div>
          ))}
        </div>
        {policy && (
          <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 flex items-start gap-2" data-testid="late-policy-info">
            <Clock size={14} className="text-[#D97706] shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#92400E]">Late Deduction Policy</p>
              <p className="text-xs text-[#52525B] mt-0.5">{policy}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AttendancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [today, setToday] = useState(null);
  const [roster, setRoster] = useState(null);
  const [history, setHistory] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const refresh = useCallback(() => {
    api.get('/attendance/me/today').then(r => setToday(r.data)).catch(() => {});
    if (isAdmin) api.get('/attendance/today').then(r => setRoster(r.data)).catch(() => {});
    api.get(`/attendance?month=${month}`).then(r => setHistory(r.data || [])).catch(() => {});
  }, [isAdmin, month]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="attendance-page">
      <MyStatsBar />
      <PunchCard today={today} onPunched={refresh} />

      {isAdmin && (
        <>
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B] mt-2">Today's Roster</p>
          <TodayRoster data={roster} onRefresh={refresh} />
        </>
      )}

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <p className="text-sm font-semibold text-[#0A0A0A]">{isAdmin ? 'All Records' : 'My Attendance'}</p>
            <div className="flex items-center gap-2">
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Month</Label>
              <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-lg w-40 h-8 text-xs" data-testid="month-filter" />
            </div>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-[#52525B] text-center py-6">No records for this month.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                  {isAdmin && <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Staff</TableHead>}
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">In</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Out</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Worked</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Late</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Remark</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(r => (
                  <TableRow key={r.id} className="border-[#E4E4E7]" data-testid={`att-row-${r.id}`}>
                    <TableCell className="text-xs font-mono">{fmtDate(r.date)}</TableCell>
                    {isAdmin && <TableCell className="text-xs font-medium">{r.user_name}</TableCell>}
                    <TableCell><StatusBadge status={r.status} isLate={r.is_late} /></TableCell>
                    <TableCell className="text-xs font-mono">{fmtTime(r.check_in)}</TableCell>
                    <TableCell className="text-xs font-mono">{fmtTime(r.check_out)}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.total_working_hours ? `${r.total_working_hours}h` : '—'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.late_minutes ? `${r.late_minutes}m` : '—'}</TableCell>
                    <TableCell className="text-xs text-[#52525B] max-w-[200px] truncate" title={r.remark || ''}>{r.remark || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {r.admin_edited && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#F59E0B]" onClick={() => setHistoryTarget(r.id)} title="View audit history" data-testid={`history-${r.id}`}>
                            <ListBullets size={14} />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditTarget(r)} data-testid={`edit-row-${r.id}`}>
                            <PencilSimple size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AttendanceEditDialog record={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); refresh(); }} />
      <HistoryDialog recordId={historyTarget} onClose={() => setHistoryTarget(null)} />
    </div>
  );
}
